'use client';

import * as React from 'react';
import { cx } from '../utils';
import { usePopover, Popover } from './Popover';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* ── DropdownMenu ──────────────────────────────────── */

function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(node);
      else (ref as React.MutableRefObject<T>).current = node;
    }
  };
}

export interface DropdownMenuProps extends WithChildren {
  trigger: React.ReactElement<Record<string, unknown>>;
  align?: 'start' | 'end' | 'center';
  className?: string;
}

export function DropdownMenu({ trigger, children, align = 'start', className }: DropdownMenuProps) {
  const pop = usePopover();
  const menuRef = React.useRef<HTMLDivElement>(null);

  /* Focus first enabled item on open. */
  React.useEffect(() => {
    if (pop.isOpen && menuRef.current) {
      const first = menuRef.current.querySelector<HTMLElement>('.j-menu-item:not(:disabled)');
      if (first) first.focus();
    }
  }, [pop.isOpen]);

  const onMenuKey = (e: React.KeyboardEvent) => {
    const items = menuRef.current?.querySelectorAll<HTMLElement>('.j-menu-item:not(:disabled)');
    if (!items?.length) return;
    const arr = Array.from(items);
    const idx = arr.indexOf(document.activeElement as HTMLElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      arr[(idx + 1) % arr.length]!.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      arr[(idx - 1 + arr.length) % arr.length]!.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      arr[0]!.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      arr[arr.length - 1]!.focus();
    }
  };

  const triggerProps = trigger.props as {
    onClick?: React.MouseEventHandler<HTMLElement>;
  };
  const triggerRef = (trigger as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;

  const triggerEl = React.cloneElement(trigger, {
    ref: composeRefs(triggerRef, pop.anchorRef),
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      triggerProps.onClick?.(event);
      if (event.defaultPrevented) return;
      if (pop.isOpen) pop.close();
      else pop.open();
    },
    'aria-haspopup': 'menu',
    'aria-expanded': pop.isOpen,
  } as Record<string, unknown>);

  return (
    <>
      {triggerEl}
      <Popover {...pop.popoverProps} align={align} trapFocus className={className} panelRole={null}>
        <div ref={menuRef} className="j-menu" role="menu" tabIndex={-1} onKeyDown={onMenuKey}>
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;
            if (child.type === MenuItem) {
              return React.cloneElement(child as React.ReactElement<MenuItemProps>, {
                _onClose: pop.close,
              });
            }
            return child;
          })}
        </div>
      </Popover>
    </>
  );
}

/* ── MenuItem ──────────────────────────────────────── */

export interface MenuItemProps extends StyledProps, UnsafeStyleProps {
  label: React.ReactNode;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** @internal Injected by DropdownMenu to close on click. */
  _onClose?: () => void;
}

export function MenuItem({
  label,
  icon,
  description,
  onClick,
  destructive,
  disabled,
  _onClose,
  className,
  unsafe_style,
}: MenuItemProps) {
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    _onClose?.();
  };

  return (
    <button
      type="button"
      className={cx('j-menu-item', destructive && 'j-menu-item-destructive', className)}
      role="menuitem"
      disabled={disabled}
      onClick={handleClick}
      tabIndex={-1}
      style={unsafe_style}
    >
      {icon && <span className="j-menu-item-icon">{icon}</span>}
      <div>
        <div className="j-menu-item-label">{label}</div>
        {description && <div className="j-menu-item-desc">{description}</div>}
      </div>
    </button>
  );
}

/* ── MenuDivider ───────────────────────────────────── */

export function MenuDivider() {
  return <div className="j-menu-divider" role="separator" />;
}
